import useGetMe from '@/api/queries/me/useGetMe';
import useAgreeToTermsAndPrivacyVersion from '@/api/rest/termsAndPrivacy/useAgreeToTermsAndPrivacyVersion';
import useGetTermsAndPrivacyUpdates from '@/api/rest/termsAndPrivacy/useGetTermsAndPrivacyUpdates';
import {
  clearLocalStorage,
  clearLocalStorageIfAccountChanged,
} from '@/helpers/localStorageKeyHelper';
import {
  AccountAccessBanner,
  MaintenanceBanner,
  OfflineBanner,
} from '@/ui/components';
import ChatAgent from '@/ui/components/chatAgent/ChatAgent';
import ChatAgentContextProvider from '@/ui/components/chatAgent/ChatAgentContextProvider';
import CustomFieldDateFormatContextProvider from '@/ui/components/contextProviders/CustomFieldDateFormatContextProvider';
import SessionStatusBanner from '@/ui/layouts/rootLayout/components/SessionStatusBanner';
import TermsAndPrivacyModal from '@/ui/layouts/rootLayout/components/TermsAndPrivacyModal';
import UpdateAvailableBanner from '@/ui/layouts/rootLayout/components/UpdateAvailableBanner';
import useDevelopmentTools from '@/ui/layouts/rootLayout/components/useDevelopmentTools';
import { OverlayPortalContainerProvider } from '@payaca/ambrosia';
import moment from 'moment-timezone';
import { FC, PropsWithChildren, useEffect, useRef } from 'react';

const AuthenticatedRootLayout: FC<PropsWithChildren> = (props) => {
  const { children } = props;

  useDevelopmentTools();

  const { hasChatAgentAccess, user } = useGetMe();

  // Detect account switches (e.g. via another tab or the admin app). On first load we
  // record the baseline; if the active account later changes while this tab is open we
  // reset all client state the same way the initiating tab does.
  const loadedAccountIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const accountId = user?.account.id;
    if (!accountId) return;

    if (loadedAccountIdRef.current === undefined) {
      loadedAccountIdRef.current = accountId;
      clearLocalStorageIfAccountChanged(accountId);
      return;
    }

    if (loadedAccountIdRef.current !== accountId) {
      clearLocalStorage();
      window.location.reload();
    }
  }, [user?.account.id]);

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    moment.updateLocale('en', { week: { dow: 1 } });
  }, []);

  // Terms & Privacy
  const { data: termsUpdate } = useGetTermsAndPrivacyUpdates();
  const agreeMutation = useAgreeToTermsAndPrivacyVersion();

  return (
    <CustomFieldDateFormatContextProvider>
      <ChatAgentContextProvider>
        <OverlayPortalContainerProvider value={contentRef}>
          <div
            className="grid h-svh overflow-hidden bg-gray-a7"
            style={{
              gridTemplateRows: 'auto 1fr',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              gridTemplateAreas: `
            "banner  banner"
            "content sidebar"
          `,
            }}
          >
            <div style={{ gridArea: 'banner' }}>
              <OfflineBanner />
              <UpdateAvailableBanner />
              <MaintenanceBanner />
              <SessionStatusBanner />
              <AccountAccessBanner />
              <div id="banner-portal" />
            </div>
            {/* `contain: layout` makes this cell the containing block for the
              fixed overlays portalled into it via OverlayPortalContainerProvider,
              so they fill this column rather than the viewport. `overflow-clip`
              clips them to it WITHOUT making this a scroll container — `hidden`
              would, and the browser would then scroll it to reveal an overlay
              that takes focus while still animating in off-screen, lurching the
              page content sideways. */}
            <div
              ref={contentRef}
              className="min-w-0 min-h-0 overflow-clip"
              style={{ gridArea: 'content', contain: 'layout' }}
            >
              {children}
            </div>
            {hasChatAgentAccess && (
              <div
                className="min-w-0 min-h-0 overflow-hidden"
                style={{ gridArea: 'sidebar' }}
              >
                <ChatAgent />
              </div>
            )}
          </div>

          <TermsAndPrivacyModal
            isOpen={!!termsUpdate}
            termsMarkdown={termsUpdate?.termsMarkdown}
            privacyMarkdown={termsUpdate?.privacyMarkdown}
            onAgree={() => {
              if (termsUpdate?.version) {
                agreeMutation.mutate(termsUpdate.version);
              }
            }}
            isAgreeing={agreeMutation.isPending}
          />
        </OverlayPortalContainerProvider>
      </ChatAgentContextProvider>
    </CustomFieldDateFormatContextProvider>
  );
};

export default AuthenticatedRootLayout;
